import { useState } from "react";
import { Typography, Icon, Switch, Divider } from "@trimble/modus-cmx";
import { Stack } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { format } from "@/utils";
import { useUpdateLicenseAutoRevoke } from "@/hooks/service/useUpdateLicenseAutoRevoke";
import {
  PermissionAction,
  PermissionCategory,
  PermissionGate,
} from "@/permissions";

type OfflineLicenseDetailsProps = {
  /**
   * EMS license id + customer account id drive the auto-revoke toggle
   * mutation. An OfflineLicense always has an id and is always scoped to a
   * customer, so both are required; if either arrives empty at runtime the
   * row renders nothing (bad data from the backend).
   */
  licenseId: string;
  customerAccountId: string;
  checkOutDate: string;
  checkInDate: string;
  revokeWhenOnline: boolean;
  disabled?: boolean;
  /**
   * Render a horizontal divider above the row. Defaults to true so the row
   * is visually separated from the card content above it (matches the UX
   * spec for the contract / user assignment cards).
   */
  showDivider?: boolean;
  /**
   * Per the Figma:
   *  - `"user-card"` (default) reads "License Offline from: …" — used on
   *    user assignment cards where the surrounding context doesn't already
   *    say which thing is offline.
   *  - `"subscription-card"` reads "Offline from: …" — used on contract /
   *    subscription cards where the card already shows the product, so the
   *    shorter prefix is enough.
   */
  variant?: "user-card" | "subscription-card";
};

const OfflineLicenseDetails = (props: OfflineLicenseDetailsProps) => {
  const {
    licenseId,
    customerAccountId,
    checkOutDate,
    checkInDate,
    revokeWhenOnline,
    disabled = false,
    showDivider = true,
    variant = "user-card",
  } = props;
  const { t } = useTranslation();

  // Optimistic local state so the toggle flips immediately on click. We
  // revert on error; on success the mutation's returned OfflineLicense
  // updates the normalized cache, keeping the prop value in line.
  const [optimisticValue, setOptimisticValue] = useState(revokeWhenOnline);
  // Re-sync the optimistic value whenever the server value changes underneath
  // us (cache update / refetch, or another part of the UI mutating the same
  // license). React's "adjust state while rendering" pattern is preferred here
  // over a useEffect: the update happens during render with no extra paint.
  const [syncedRevokeWhenOnline, setSyncedRevokeWhenOnline] =
    useState(revokeWhenOnline);
  if (revokeWhenOnline !== syncedRevokeWhenOnline) {
    setSyncedRevokeWhenOnline(revokeWhenOnline);
    setOptimisticValue(revokeWhenOnline);
  }
  const [updateLicenseAutoRevoke, { loading }] = useUpdateLicenseAutoRevoke();

  const labelKey =
    variant === "subscription-card"
      ? "devices.manage_device_modal.offline_from_to"
      : "devices.manage_device_modal.license_offline_from_to";

  // An offline license always has an id and is always scoped to a customer.
  // Empty values mean bad backend data — render nothing rather than a row
  // whose toggle can't work. (Types require the props; this guards runtime
  // data, e.g. empty strings from GraphQL.)
  if (!licenseId || !customerAccountId) {
    return null;
  }

  const handleToggle = async () => {
    if (loading || disabled) {
      return;
    }
    const nextValue = !optimisticValue;
    setOptimisticValue(nextValue);
    try {
      await updateLicenseAutoRevoke({
        variables: {
          customerAccountId,
          licenseId,
          revokeAtCheckIn: nextValue,
        },
      });
    } catch {
      // useMutation's onError already toasts; revert optimistic state so
      // the visible toggle matches the server value the user last saw.
      setOptimisticValue((prev) => !prev);
    }
  };

  return (
    <Stack sx={{ marginTop: 1 }} data-testid="offline-license-details">
      {showDivider && <Divider style={{ marginBottom: "0rem" }} />}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ color: "var(--modus-wc-color-gray-6)" }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Icon name="wifi_off" variant="solid" size="xs" />
          <Typography size="md">
            <Trans
              parent="span"
              i18nKey={labelKey}
              values={{
                checkOutDate: format.formatDate(checkOutDate),
                checkInDate: format.formatDate(checkInDate),
              }}
              components={{ strong: <strong /> }}
            />
          </Typography>
        </Stack>
        <PermissionGate
          resource={PermissionCategory.Subscription}
          action={PermissionAction.CreateAndManage}
        >
          <Switch
            size="sm"
            value={optimisticValue}
            disabled={loading || disabled}
            onInputChange={handleToggle}
            label={t("devices.manage_device_modal.revoke_when_online")}
          />
        </PermissionGate>
      </Stack>
    </Stack>
  );
};

export default OfflineLicenseDetails;
